import * as SVG from 'svg.js';
import { Point, Region } from './region';

export enum CropHandlePosition
{
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
}

interface HandleCornerFeatures
{
  extraCssClass: string;
  rotation: number;
}

function getCornerFeatures(position: CropHandlePosition): HandleCornerFeatures
{
  switch (position) {
    case CropHandlePosition.TopLeft:
      return { extraCssClass: 'corner-top-left', rotation: 0 };

    case CropHandlePosition.TopRight:
      return { extraCssClass: 'corner-top-right', rotation: 90 };

    case CropHandlePosition.BottomLeft:
      return { extraCssClass: 'corner-bottom-left', rotation: 270 };

    case CropHandlePosition.BottomRight:
      return { extraCssClass: 'corner-bottom-right', rotation: 180 };

    default: return undefined; // unreachable
  }
}

export class CropHandleCorner
{
  private static readonly BASE_SIZE: number = 24;
  private readonly cornerFeatures: HandleCornerFeatures;

  private _shape: SVG.G;
  private _sideLength: number = 0;

  constructor(
      private readonly svgDoc: SVG.Doc,
      private readonly container: SVG.Element,
      public readonly position: CropHandlePosition)
  {
    this.cornerFeatures = getCornerFeatures(position);

    this._shape = this.svgDoc.group();
    this.fillShape();
  }

  public get sideLength(): number
  {
    return this._sideLength;
  }

  public get shape(): SVG.Element
  {
    return this._shape;
  }

  private fillShape(): void
  {
    const unit = this.sideLength;
    const half = unit / 2;

    this._shape
      .rect(unit, unit)
      .addClass('image-cropper-corner-area')
      .addClass(this.cornerFeatures.extraCssClass);

    this._shape
      .line(0, 0, unit, 0)
      .addClass('image-cropper-corner-line')
      .rotate(this.cornerFeatures.rotation, half, half);

    this._shape
      .line(0, 0, 0, unit)
      .addClass('image-cropper-corner-line')
      .rotate(this.cornerFeatures.rotation, half, half);
  }

  private updateSideLength(
      widthConstraint: number, heightConstraint: number): number
  {
    const newSideLength =
        Math.floor(
            Math.min(
                CropHandleCorner.BASE_SIZE,
                Math.max(
                    widthConstraint / 3,
                    heightConstraint / 3,
                    4)));

    if (this.sideLength !== newSideLength) {
      this._sideLength = newSideLength;

      this._shape.clear();
      this.fillShape();
    }

    return this.sideLength;
  }

  public update(): void
  {
    const containerBounds = this.container.bbox();

    this._sideLength =
        this.updateSideLength(containerBounds.width, containerBounds.height);

    const x =
        (this.position === CropHandlePosition.TopLeft ||
         this.position === CropHandlePosition.BottomLeft )
            ? containerBounds.x
            : containerBounds.x
                + containerBounds.width
                - this.sideLength;

    const y =
        (this.position === CropHandlePosition.TopLeft ||
         this.position === CropHandlePosition.TopRight )
            ? containerBounds.y
            : containerBounds.y
                + containerBounds.height
                - this.sideLength;

    this._shape.move(x, y);
  }
}

export interface OppositeCornersInfo
{
  same: Point;
  opposite: Point;
}

export function getOppositeCorners(
    region: Region,
    position: CropHandlePosition): OppositeCornersInfo
{
  const x = region.x;
  const y = region.y;
  const w = region.width;
  const h = region.height;

  return {
      opposite: (() => {
          switch (position) {
            case CropHandlePosition.TopLeft: return new Point(x + w, y + h);
            case CropHandlePosition.TopRight: return new Point(x, y + h);
            case CropHandlePosition.BottomLeft: return new Point(x + w, y);
            case CropHandlePosition.BottomRight: return new Point(x, y);
            default: return undefined; // unreachable
          }})(),

      same: (() => {
          switch (position) {
            case CropHandlePosition.TopLeft: return new Point(x, y);
            case CropHandlePosition.TopRight: return new Point(x + w, y);
            case CropHandlePosition.BottomLeft: return new Point(x, y + h);
            case CropHandlePosition.BottomRight: return new Point(x + w, y + h);
            default: return undefined; // unreachable
          }})(),
      };
}



// WEBPACK FOOTER //
// ./src/app/controls/image-cropper/crop-handle-corner.ts